import { Trans, useLingui } from "@lingui/react/macro";
import { useEffect, useState } from "react";
import { apiLogin, getInfoFromToken } from "@/apiCalls";
import useAccountInfo from "@/hooks/useAccountInfo";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import { useForm } from "@mantine/form";
import * as Sentry from "@sentry/react";
import { AuthFormWrapper } from "../AuthFormWrapper";
import { addToast, Button } from "@heroui/react";
import { Eye, EyeOff, KeyRound, ShieldCheck } from "lucide-react";
import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  WebAuthnAbortService,
} from "@simplewebauthn/browser";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeInputOtp } from "@/Components/NextBase/LimeInputOtp";
import { api } from "@/lib/api-client";
import { describeErrorForLog } from "@/utils/sentry-error";
import { sentryLog } from "@/utils/sentry-utils";
import { useTrackingCode } from "react-hubspot-tracking-code-hook";
import { TurnstileCompat } from "@/Components/TurnstileCompat";
import { isMobileSafari } from "react-device-detect";
import { isNativePlatform } from "@/utils/native";
import { resolvePasskeyFailure } from "./passkeyFailure";

type LoginProps = {
  setAuth: (value: boolean) => void;
  setUserToken: (value: string) => void;
  setOnboardingDone: (value: boolean) => void;
};

const Login = (props: LoginProps) => {
  const { t } = useLingui();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);

  const isTurnstileDisabled = import.meta.env.VITE_DISABLE_TURNSTILE === "true";

  const [mfaState, setMfaState] = useState<{
    preAuthToken: string;
    totpCode: string;
    recoveryCode: string;
    useRecovery: boolean;
  } | null>(null);
  const [isMfaVerifying, setIsMfaVerifying] = useState(false);

  const { mutateAsync: deleteCookie } = api.cookie.useDeleteHttpCookie();
  const { mutateAsync: verifyMfa } = api.mfa.useMfaVerify();
  const { mutateAsync: recoverMfa } = api.mfa.useMfaRecovery();
  const { mutateAsync: getPasskeyOptions } =
    api.webauthn.useWebauthnAuthenticateOptions();
  const { mutateAsync: verifyPasskey } =
    api.webauthn.useWebauthnAuthenticateVerify();
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const passkeysSupported = browserSupportsWebAuthn();

  const { setIdentity } = useTrackingCode();

  const form = useForm({
    initialValues: {
      email: "",
      password: "",
      rememberMe: false,
    },

    validate: {
      email: (value) =>
        /^\S+@\S+$/.test(value.trim()) ? null : t`Neveljaven e-poštni naslov`,
      password: (value) => (value.length > 0 ? null : t`Geslo je prekratko`),
    },
  });

  const setAccountInfo = useAccountInfo((state) => state.setAccountInfo);

  const initLoadPermissions = useUserPermissionsStore(
    (state) => state.initLoadPermissions,
  );

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const completeLoginWithToken = async (token: string) => {
    const verify = await getInfoFromToken(token);
    setAccountInfo(verify.data[0]);

    props.setOnboardingDone(verify.data[0].onboardingDone);
    props.setUserToken(token);
    localStorage.setItem("usr_token", token);

    Sentry.setContext("user", {
      clientId: verify.data[0].clientId,
      userId: verify.data[0].userId,
      pricingTierId: verify.data[0].FK_pricingTierId,
      onboardingDone: verify.data[0].onboardingDone,
    });

    Sentry.setTag("clientId", verify.data[0].clientId);

    setIdentity(verify.data[0].userEmail, {
      clientId: verify.data[0].clientId,
      userId: verify.data[0].userId,
      pricingTierId: verify.data[0].FK_pricingTierId,
      email: verify.data[0].userEmail,
      name: verify.data[0].userName,
      lastName: verify.data[0].userLastName,
      gsm: verify.data[0].userPhone,
    });

    localStorage.removeItem("pos-businessPremiseId");
    localStorage.removeItem("pos-electronicDeviceId");

    try {
      await deleteCookie({ cookieName: "pos-token" });
    } catch (error) {
      console.warn("Failed to delete pos-token cookie:", error);
    }

    await initLoadPermissions();
    props.setAuth(true);
  };

  const onSubmitForm = async (values: typeof form.values) => {
    sentryLog.info("Login form submitted", { email: values.email });
    setIsLoading(true);

    try {
      const data = {
        email: values.email.trim(),
        password: values.password.trim(),
        rememberMe: values.rememberMe ?? false,
        turnstileToken,
      };

      const isAuth = await apiLogin(data);
      const isAuthData = isAuth.data;

      if (
        isAuthData &&
        typeof isAuthData === "object" &&
        isAuthData.mfaRequired
      ) {
        if (
          typeof isAuthData.preAuthToken !== "string" ||
          isAuthData.preAuthToken.trim().length === 0
        ) {
          sentryLog.error("Login form error", {
            error: "Missing MFA pre-auth token",
          });
          addToast({
            title: t`Napaka`,
            description: t`MFA setup could not be started. Please try again.`,
            color: "danger",
          });
          setIsLoading(false);
          return;
        }

        setMfaState({
          preAuthToken: isAuthData.preAuthToken,
          totpCode: "",
          recoveryCode: "",
          useRecovery: false,
        });
        setIsLoading(false);
        return;
      }

      const token =
        typeof isAuthData === "string" ? isAuthData : isAuthData?.token;

      if (token && token !== "") {
        await completeLoginWithToken(token);
      } else {
        sentryLog.error("Login form error", { error: "Invalid token" });
        localStorage.removeItem("usr_token");
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      sentryLog.error("Login form error", describeErrorForLog(e));
      localStorage.removeItem("usr_token");

      const errorData = e.response?.data;
      const errorMessage =
        typeof errorData === "string"
          ? errorData
          : (errorData?.error ?? t`Napaka`);

      addToast({
        title: t`Napaka`,
        description: errorMessage,
        color: "danger",
      });

      // Wrong credentials / validation return 4xx — expected, and already shown
      // to the user via the toast above. Only report unexpected failures
      // (5xx, network, token problems) to Sentry.
      const status = e?.response?.status;
      const isExpectedClientError =
        typeof status === "number" && status >= 400 && status < 500;
      if (!isExpectedClientError) {
        Sentry.captureException(e, { tags: { page: "Login" } });
      }

      if (!isTurnstileDisabled) {
        setTurnstileToken(null);
        setTurnstileKey((prev) => prev + 1);
      }
    }
    setIsLoading(false);
  };

  const onSubmitMfa = async () => {
    if (!mfaState || isMfaVerifying) return;
    setIsMfaVerifying(true);

    let result: { accessToken: string; refreshToken?: string };
    try {
      if (mfaState.useRecovery) {
        result = await recoverMfa({
          preAuthToken: mfaState.preAuthToken,
          recoveryCode: mfaState.recoveryCode,
        });
      } else {
        result = await verifyMfa({
          preAuthToken: mfaState.preAuthToken,
          code: mfaState.totpCode,
        });
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      const message = e?.response?.data?.error ?? t`Invalid verification code`;
      addToast({
        title: t`Verification failed`,
        description: message,
        color: "danger",
      });
      setIsMfaVerifying(false);
      return;
    }

    if (result.refreshToken) {
      localStorage.setItem("usr_refresh_token", result.refreshToken);
    } else {
      localStorage.removeItem("usr_refresh_token");
    }

    try {
      await completeLoginWithToken(result.accessToken);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      addToast({
        title: t`Napaka`,
        description: e?.message ?? t`Login failed after verification`,
        color: "danger",
      });
    }
    setIsMfaVerifying(false);
  };

  const runPasskeyCeremony = async (useBrowserAutofill: boolean) => {
    let result: { accessToken: string; refreshToken?: string };
    try {
      const { options, authenticationToken } = await getPasskeyOptions({});
      const assertion = await startAuthentication({
        optionsJSON: options,
        useBrowserAutofill,
      });
      result = await verifyPasskey({
        authenticationToken,
        response: assertion,
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      const outcome = resolvePasskeyFailure(e, {
        conditional: useBrowserAutofill,
      });

      if (outcome.toast === "unsupported") {
        addToast({
          title: t`Passkey sign-in failed`,
          description: t`Passkeys are not available in this browser or on this device. Please use your password.`,
          color: "danger",
        });
      } else if (outcome.toast === "failed") {
        addToast({
          title: t`Passkey sign-in failed`,
          description:
            e?.response?.data?.error ??
            t`The passkey prompt was dismissed or timed out. Please try again or use your password.`,
          color: "danger",
        });
      }

      if (outcome.report === "log") {
        sentryLog.warn("Passkey sign-in rejected by the browser", {
          ...describeErrorForLog(e),
          conditional: useBrowserAutofill,
        });
      } else if (outcome.report === "issue") {
        Sentry.captureException(e, {
          tags: { page: "Login", flow: "passkey" },
        });
      }
      return;
    }

    if (result.refreshToken) {
      localStorage.setItem("usr_refresh_token", result.refreshToken);
    }

    try {
      await completeLoginWithToken(result.accessToken);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      addToast({
        title: t`Napaka`,
        description: e?.message ?? t`Login failed after verification`,
        color: "danger",
      });
    }
  };

  const onPasskeyLogin = async () => {
    if (isPasskeyLoading) return;
    setIsPasskeyLoading(true);
    await runPasskeyCeremony(false);
    setIsPasskeyLoading(false);
  };

  // Conditional UI: the passkey is offered inside the email field's autofill
  // list rather than only behind a button, so it shows up while the user is
  // identifying themselves and stays absent on a device holding no passkey.
  // The request sits pending until they pick one, so it has to be cancelled on
  // unmount or the next ceremony inherits a wedged one.
  // biome-ignore lint/correctness/useExhaustiveDependencies: arming is once per page load; re-running on a re-created runPasskeyCeremony would cancel and re-issue the pending request every render.
  useEffect(() => {
    if (!passkeysSupported) return;

    let active = true;

    void (async () => {
      const supported = await browserSupportsWebAuthnAutofill();
      if (!supported || !active) return;
      await runPasskeyCeremony(true);
    })();

    return () => {
      active = false;
      WebAuthnAbortService.cancelCeremony();
    };
  }, []);

  const pageTitle = t`Prijava`;

  if (mfaState) {
    return (
      <>
        <title>{pageTitle}</title>
        <AuthFormWrapper isLogin={true}>
          <div className="flex flex-col items-center gap-1">
            <ShieldCheck className="text-primary" size={32} />
            <p className="text-lg font-semibold">
              <Trans>Two-Factor Authentication</Trans>
            </p>
            <p className="text-default-500 text-center text-sm">
              {mfaState.useRecovery ? (
                <Trans>Enter one of your recovery codes.</Trans>
              ) : (
                <Trans>
                  Enter the 6-digit code from your authenticator app.
                </Trans>
              )}
            </p>
          </div>

          {mfaState.useRecovery ? (
            <LimeInput
              label={t`Recovery code`}
              data-identifier="mfa-recovery-input"
              size="sm"
              className="w-full"
              value={mfaState.recoveryCode}
              onChange={(e) =>
                setMfaState((s) =>
                  s ? { ...s, recoveryCode: e.target.value } : s,
                )
              }
              autoFocus={!isMobileSafari && !isNativePlatform()}
              onKeyDown={(e) => {
                if (e.key === "Enter" && mfaState.recoveryCode.length > 0) {
                  onSubmitMfa();
                }
              }}
            />
          ) : (
            <div className="flex justify-center">
              <LimeInputOtp
                length={6}
                variant="bordered"
                value={mfaState.totpCode}
                onChange={(val: string) =>
                  setMfaState((s) => (s ? { ...s, totpCode: val } : s))
                }
                onComplete={onSubmitMfa}
                data-identifier="mfa-totp-input"
                autoFocus={!isMobileSafari && !isNativePlatform()}
              />
            </div>
          )}

          <Button
            fullWidth
            color="primary"
            isLoading={isMfaVerifying}
            isDisabled={
              mfaState.useRecovery
                ? mfaState.recoveryCode.length === 0
                : mfaState.totpCode.length !== 6
            }
            onPress={onSubmitMfa}
            data-identifier="submit-mfa-button"
          >
            <Trans>Verify</Trans>
          </Button>

          <button
            type="button"
            className="text-primary text-sm hover:underline"
            onClick={() =>
              setMfaState((s) =>
                s ? { ...s, useRecovery: !s.useRecovery } : s,
              )
            }
          >
            {mfaState.useRecovery ? (
              <Trans>Use authenticator app instead</Trans>
            ) : (
              <Trans>Use a recovery code</Trans>
            )}
          </button>

          <button
            type="button"
            className="text-default-400 text-sm hover:underline"
            onClick={() => setMfaState(null)}
          >
            <Trans>Back to login</Trans>
          </button>
        </AuthFormWrapper>
      </>
    );
  }

  return (
    <>
      <title>{pageTitle}</title>
      <AuthFormWrapper isLogin={true}>
        <form
          style={{ display: "contents" }}
          onSubmit={form.onSubmit(onSubmitForm)}
          noValidate
        >
          <LimeInput
            label={t`E-poštni naslov`}
            data-identifier="email-input"
            className="w-full"
            {...form.getInputProps("email")}
            autoComplete="email webauthn"
            name="email"
          />

          <LimeInput
            label={t`Geslo`}
            data-identifier="password-input"
            className={"w-full"}
            {...form.getInputProps("password")}
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            name="password"
            endContent={
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="focus:outline-hidden"
                aria-label="Toggle password visibility"
              >
                {showPassword ? (
                  <Eye className="text-default-400 pointer-events-none" />
                ) : (
                  <EyeOff className="text-default-400 pointer-events-none" />
                )}
              </button>
            }
          />

          <LimeSwitch
            title={t`Ostani prijavljen`}
            {...form.getInputProps("rememberMe", {
              type: "checkbox",
            })}
            size="sm"
            isCompact
          />

          {!isTurnstileDisabled && (
            <TurnstileCompat
              key={turnstileKey}
              siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
              onSuccess={(token) => setTurnstileToken(token)}
              onExpire={() => setTurnstileToken(null)}
              onError={(error) => {
                console.error(error);
                setTurnstileToken(null);
              }}
            />
          )}
          <Button
            fullWidth
            type="submit"
            isLoading={isLoading}
            color="primary"
            data-identifier="submit-login-button"
          >
            <Trans>Nadaljuj</Trans>
          </Button>
        </form>

        {passkeysSupported && (
          <>
            <div className="flex w-full items-center gap-3">
              <div className="bg-default-200 h-px flex-1" />
              <span className="text-default-400 text-xs">
                <Trans>or</Trans>
              </span>
              <div className="bg-default-200 h-px flex-1" />
            </div>

            <Button
              fullWidth
              variant="bordered"
              startContent={<KeyRound size={18} />}
              isLoading={isPasskeyLoading}
              onPress={onPasskeyLogin}
              data-identifier="passkey-login-button"
            >
              <Trans>Prijavite se s ključem za dostop</Trans>
            </Button>
          </>
        )}
      </AuthFormWrapper>
    </>
  );
};

export default Login;
